import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell, PillarSection, TransparencyNote } from "@/components/PageShell";
import { TravelSection } from "@/components/TravelSection";
import { Reveal } from "@/components/Reveal";
import { ConsultationModeBadge } from "@/components/ConsultationModeBadge";

// Visuels FACOW360 — Magnétisme (humain & énergie universelle)
const IMG_CANAL = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/wi8l7qe1_image%20magnetisme%201.jpg";   // séance classique — flux cyan/or, sans contact
const IMG_UNIVERSEL = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/80zt5ppw_image%20magnetisme%202.jpg"; // temple d'énergie — pluri-origines, géométrie sacrée

// Triptyque "Universalité" — toutes les origines, tous les contextes
const IMG_NATURE = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/hhzh8cwx_Sans%20titre99.jpg";            // littoral, ancrage marin
const IMG_TECH = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/szsz24zf_Sans%20titre9.jpg";               // espace futuriste, prana-flow & cristaux
const IMG_CORPORATE = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/ny380oy3_88.jpg";                    // régénération dirigeant, contexte corporate

const HeroVisual = ({ src, alt, caption, testId }) => (
  <Reveal direction="zoom" duration={1.05} amount={0.22}>
    <figure
      className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-[#0F1A3A] mt-8 group"
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-transparent to-transparent opacity-70" />
      <figcaption className="absolute bottom-0 left-0 right-0 px-6 py-4">
        <span className="inline-block px-3 py-1.5 rounded-full bg-[#0F1A3A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono-tech uppercase tracking-[0.25em]">
          {caption}
        </span>
      </figcaption>
    </figure>
  </Reveal>
);

export default function Magnetisme() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";

  return (
    <PageShell
      testId="page-magnetisme"
      title={isFr ? "Magnétisme" : "Magnetism"}
      subtitle={isFr
        ? "Une méthode naturelle qui se concentre sur l'humain et sur l'énergie universelle qui relie tous les êtres vivants. Aucune frontière de culture, d'âge ou d'espèce — uniquement la circulation du vivant."
        : "A natural method focused on the human being and on the universal energy that connects every living being. No frontier of culture, age or species — only the circulation of the living."}
    >
      <ConsultationModeBadge isFr={isFr} testId="magn-consultation-badge" />

      {/* ============================ MOUVEMENT I — LE CANAL VIBRATOIRE ============================ */}

      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "I — Le praticien comme canal" : "I — The practitioner as a channel"}
          testId="magn-section-canal"
        >
          <p>
            {isFr
              ? "Le magnétisme libère les blocages et relance la circulation de l'énergie vitale pour soutenir la régénération du corps. Le praticien agit comme un canal qui capte l'énergie universelle pour la transmettre, harmonisant les chakras et l'aura."
              : "Magnetism releases blockages and restarts the flow of vital energy to support the body's regeneration. The practitioner acts as a channel that captures the universal energy and transmits it, harmonising the chakras and the aura."}
          </p>
          <p>
            {isFr
              ? "L'énergie circule visiblement entre les mains et l'aura — sans contact physique direct. Cette posture est celle d'un consultant énergétique : une lecture précise, une transmission propre, aucun geste invasif."
              : "The energy visibly flows between the hands and the aura — with no direct physical contact. This is the posture of an energetic consultant: precise reading, clean transmission, no invasive gesture."}
          </p>
        </PillarSection>
      </Reveal>

      <HeroVisual
        src={IMG_CANAL}
        alt={isFr
          ? "Séance de magnétisme FACOW360 — circulation cyan et or sans contact direct"
          : "FACOW360 magnetism session — cyan and gold energy flow, no direct contact"}
        caption={isFr ? "01 · Le canal vibratoire" : "01 · The vibrational channel"}
        testId="magn-visual-canal"
      />

      {/* ============================ MOUVEMENT II — L'ÉNERGIE QUI RELIE TOUS LES VIVANTS ============================ */}

      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "II — L'énergie qui relie tous les vivants" : "II — The energy that connects every living being"}
          testId="magn-section-universel"
        >
          <p>
            {isFr
              ? "Le magnétisme ne distingue ni la couleur de peau, ni la langue, ni l'âge, ni l'espèce. Il agit sur tout être vivant : femme africaine, homme indien, enfant asiatique, ancien oriental, jeune européen — mais aussi votre chien apaisé, votre chat ronronnant. La circulation universelle est la même."
              : "Magnetism makes no distinction of skin colour, language, age or species. It acts on every living being: an African woman, an Indian man, an Asian child, an Eastern elder, a European youth — but also your peaceful dog, your purring cat. The universal flow is the same."}
          </p>
          <p>
            {isFr
              ? "C'est cette universalité qui guide ma méthode : tisser une fusion entre les traditions héritées des continents, la géométrie sacrée et les outils modernes de lecture vibratoire. Un temple d'énergie sans frontière."
              : "This universality guides my method: weaving a fusion between traditions inherited from the continents, sacred geometry, and modern vibrational reading tools. An energy temple without borders."}
          </p>
        </PillarSection>
      </Reveal>

      <HeroVisual
        src={IMG_UNIVERSEL}
        alt={isFr
          ? "Temple d'énergie FACOW360 — fusion des origines, géométrie sacrée Metatron"
          : "FACOW360 energy temple — fusion of origins, sacred Metatron geometry"}
        caption={isFr ? "02 · Temple d'énergie · fusion des origines" : "02 · Energy temple · fusion of origins"}
        testId="magn-visual-universel"
      />

      {/* ============================ MOUVEMENT III — LE RITUEL & L'ESPACE ============================ */}

      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "III — Le rituel & l'espace de la séance" : "III — The ritual & the space of the session"}
          testId="magn-section-rituel"
        >
          <p>
            {isFr
              ? "Avant chaque séance, je me prépare — méditation, bougies, encens — et j'utilise l'ancrage à la terre ainsi qu'une bulle de protection pour ne pas absorber les énergies lourdes. Le lieu compte : plage à l'aube, jardin urbain futuriste, bureau d'entreprise, salon paisible… le rituel s'adapte à votre cadre."
              : "Before each session, I prepare myself — meditation, candles, incense — and I use earth anchoring along with a protective bubble to avoid absorbing heavy energies. The place matters: dawn beach, futuristic urban garden, corporate office, peaceful living room… the ritual adapts to your setting."}
          </p>
          <p>
            {isFr
              ? "Le travail à distance est aussi puissant que le présentiel car l'énergie suit la pensée. J'utilise une photo et l'identité du client comme adresse vibratoire — un protocole d'une précision millimétrée qui me permet d'accompagner des clients à Verviers, Liège, Narbonne… ou partout en Europe."
              : "Remote work is as powerful as on-site work because energy follows thought. I use a photo and the client's identity as a vibrational address — a millimetre-precise protocol that lets me support clients in Verviers, Liège, Narbonne… or anywhere in Europe."}
          </p>
        </PillarSection>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8" data-testid="magn-gallery-universel">
        {[
          { src: IMG_NATURE, captionFr: "Littoral · ancrage marin", captionEn: "Coastline · marine anchoring" },
          { src: IMG_TECH, captionFr: "Espace futuriste · prana-flow & cristaux", captionEn: "Futuristic space · prana-flow & crystals" },
          { src: IMG_CORPORATE, captionFr: "Régénération dirigeant · contexte corporate", captionEn: "Executive regeneration · corporate setting" },
        ].map((p, i) => (
          <Reveal key={i} direction="up" delay={i * 0.15} duration={0.9} amount={0.15}>
            <figure
              className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#0F1A3A] aspect-square h-full"
              data-testid={`magn-universel-${i + 1}`}
            >
              <img
                src={p.src}
                alt={isFr ? p.captionFr : p.captionEn}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/40 to-transparent" />
              <figcaption className="absolute bottom-3 left-3 right-3">
                <span className="block text-[11px] text-amber-glow font-display leading-snug">
                  {isFr ? p.captionFr : p.captionEn}
                </span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-500 italic">
        {isFr
          ? "Visuels originaux FACOW360 — illustration de la circulation universelle du vivant. Reproduction interdite sans autorisation."
          : "Original FACOW360 visuals — illustrating the universal circulation of the living. Reproduction prohibited without authorisation."}
      </p>

      {/* ============================ NOTE & CTA ============================ */}

      <TransparencyNote>
        {isFr
          ? "En tant que magnétiseur, je mets en œuvre des moyens techniques et sensibles guidés par la bienveillance. Ma méthode est un accompagnement énergétique — elle ne se substitue pas à un suivi médical."
          : "As a magnetiser, I deploy technical and sensitive means guided by kindness. My method is an energetic accompaniment — it does not substitute any medical follow-up."}
      </TransparencyNote>

      <TravelSection pillar="magnetisme" />

      <div className="mt-12 flex flex-wrap gap-3">
        <Link to="/devis" className="btn-gold" data-testid="magn-cta-devis">
          {isFr ? "Réserver une séance" : "Book a session"}
        </Link>
        <Link to="/bilan" className="btn-primary" data-testid="magn-cta-bilan">
          {isFr ? "Bilan de Vitalité (19 €)" : "Vitality Assessment (€19)"}
        </Link>
      </div>
    </PageShell>
  );
}
